// §4.5's run detail (`/planning/mrp/runs/$runId`, P3-H-2, D-388 ruling 8): the
// header, the trigger, the plan and the refresh the run read, its group pass
// and fixpoint, its propagation depth, conflict retries, counts, and every
// stats list beside its exact count (each list holds at most 200 entries).
// Read on `planning.planned_order.read`; procurement is served §7's subset.

import { useEffect, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { ExactQty, MrpErrorLine, MrpRunStatusChip, MrpTriggerChip } from "../components/mrp-bits";
import { useCalendarWorkCenters } from "../hooks/calendar-queries";
import { PLANNED_ORDER_KINDS, useMrpRun, type MrpRun } from "../hooks/mrp-queries";
import { useProductIndex } from "../hooks/planning-queries";
import {
  formatRunDuration,
  RUN_STAT_LISTS,
  graphExcluded,
  mrpRunDuration,
  shortId,
  statList,
  type RunStatList,
} from "../lib/mrp-lib";
import { workCenterLabel } from "../lib/calendar-lib";
import { formatDate } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";

export function PlanningMrpRunScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const runId = useParams({ strict: false }).runId ?? "";
  const run = useMrpRun(runId);

  useEffect(() => {
    if (run.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [run.error, navigate]);

  const back = (
    <Link to="/planning" data-testid="planning-mrp-run-back">
      {t("planning.mrpRun.back")}
    </Link>
  );

  if (run.error !== null && !(run.error instanceof UnauthorizedError)) {
    const missing = run.error instanceof RequestError && run.error.httpStatus === 404;
    return (
      <section className="planning-screen" data-testid="planning-mrp-run">
        <PlanningHeader title={t("planning.mrpRun.title")} />
        {missing ? (
          <p className="planning-note" data-testid="planning-mrp-run-missing">
            {t("planning.mrpRun.notFound")} {back}
          </p>
        ) : (
          <ErrorState
            title={t("planning.mrpRun.loadError")}
            retryLabel={t("planning.common.retry")}
            onRetry={() => void run.refetch()}
            requestId={requestIdOf(run.error)}
            requestIdLabel={t("planning.common.requestId")}
          />
        )}
      </section>
    );
  }

  if (run.data === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-mrp-run">
        <PlanningHeader title={t("planning.mrpRun.title")} />
        <div data-testid="planning-mrp-run-loading">
          <CardSkeleton lines={4} label={t("planning.mrpRun.loading")} />
          <CardSkeleton lines={6} label={t("planning.mrpRun.loading")} />
        </div>
      </section>
    );
  }

  return <RunDetail run={run.data} back={back} />;
}

function RunDetail({ run, back }: { run: MrpRun; back: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { facilities } = usePlanningFacility();
  const facility = facilities.find((f) => f.id === run.facility_id);
  const zone = facility?.timezone;
  const time = (iso: string | null) =>
    iso === null ? "–" : formatTimestamp(iso, i18n.language, zone);
  const stats = run.stats;
  const duration = mrpRunDuration(run);
  const orchestration = stats.orchestration;
  const amber = graphExcluded(stats);

  return (
    <section
      className="planning-screen planning-mrp-run"
      data-testid="planning-mrp-run"
      data-run-id={run.id}
      data-status={run.status}
    >
      <PlanningHeader
        title={<span data-testid="planning-mrp-run-no">{run.run_no}</span>}
        actions={
          <>
            <span className="planning-chips">
              <MrpTriggerChip trigger={run.trigger} />
              <MrpRunStatusChip status={run.status} amber={amber} />
            </span>
            {back}
          </>
        }
      />
      {run.status === "failed" ? (
        <Banner kind="bad" data-testid="planning-mrp-run-failed-banner">
          <MrpErrorLine run={run} />
        </Banner>
      ) : null}
      {amber && run.status === "succeeded" ? (
        <Banner kind="warn" data-testid="planning-mrp-run-graph-banner">
          {t("planning.mrp.graphExcluded")}
        </Banner>
      ) : null}
      {run.stats_narrowed ? (
        <p className="planning-note" data-testid="planning-mrp-run-narrowed">
          {t("planning.mrpRun.narrowed")}
        </p>
      ) : null}
      <Card title={t("planning.mrpRun.header")} data-testid="planning-mrp-run-header">
        <dl className="planning-facts">
          <Fact label={t("planning.mrpRun.facility")}>
            {facility?.name ?? shortId(run.facility_id)}
          </Fact>
          <Fact label={t("planning.mrpRun.triggerCount")}>{run.trigger_count}</Fact>
          <Fact label={t("planning.mrpRun.created")}>{time(run.created_at)}</Fact>
          <Fact label={t("planning.mrpRun.due")}>{time(run.due_at)}</Fact>
          <Fact label={t("planning.mrpRun.started")}>{time(run.started_at)}</Fact>
          <Fact label={t("planning.mrpRun.finished")}>{time(run.finished_at)}</Fact>
          {duration !== undefined ? (
            <Fact label={t("planning.mrpRun.duration")}>{formatRunDuration(t, duration)}</Fact>
          ) : null}
          <Fact label={t("planning.mrpRun.plan")}>
            {run.mps_plan_id === null ? (
              t("planning.mrpRun.noPlan")
            ) : (
              <Link
                to="/planning/mps"
                data-testid="planning-mrp-run-plan"
                data-plan-id={run.mps_plan_id}
              >
                {t("planning.mrpRun.planLink")}
              </Link>
            )}
          </Fact>
          <Fact label={t("planning.mrpRun.refresh")}>
            {run.demand_refresh_id === null ? (
              "–"
            ) : (
              <Link
                to="/planning/demand"
                data-testid="planning-mrp-run-refresh"
                data-refresh-id={run.demand_refresh_id}
              >
                {t("planning.mrpRun.refreshLink")}
              </Link>
            )}
          </Fact>
          <Fact label={t("planning.mrpRun.horizon")}>
            {run.params.horizon_weeks !== undefined
              ? t("planning.mrpRun.weeks", { count: run.params.horizon_weeks })
              : "–"}
          </Fact>
          <Fact label={t("planning.mrpRun.orchestration")}>
            {run.orchestration_id === null ? (
              t("planning.mrpRun.single")
            ) : (
              <span
                data-testid="planning-mrp-run-orchestration"
                data-orchestration-id={run.orchestration_id}
              >
                {t("planning.mrpRun.pass", {
                  pass: run.pass_no ?? orchestration?.pass_no ?? 1,
                })}
                {orchestration?.fixpoint != null ? (
                  <>
                    {" "}
                    ·{" "}
                    {t(`planning.mrp.fixpoint.${orchestration.fixpoint}`, {
                      pass: orchestration.pass_no,
                    })}
                  </>
                ) : null}
              </span>
            )}
          </Fact>
          <Fact label={t("planning.mrpRun.depth")}>
            <span data-testid="planning-mrp-run-depth">{run.propagation.depth}</span>
          </Fact>
          {stats.conflict_retries !== undefined ? (
            <Fact label={t("planning.mrpRun.conflictRetries")}>
              <span data-testid="planning-mrp-run-retries">{stats.conflict_retries}</span>
            </Fact>
          ) : null}
          {stats.max_low_level_code !== undefined ? (
            <Fact label={t("planning.mrpRun.levels")}>{stats.max_low_level_code}</Fact>
          ) : null}
        </dl>
      </Card>
      <Counts run={run} />
      <StatLists run={run} />
      <p className="planning-actions">
        <Link to="/planning/orders" search={{ run: run.id }} data-testid="planning-mrp-run-orders">
          {t("planning.mrpRun.ordersOfRun")}
        </Link>
      </p>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="planning-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Counts({ run }: { run: MrpRun }) {
  const { t } = useTranslation();
  const s = run.stats;
  const cells: { key: string; value: number | undefined }[] = [
    { key: "productsPlanned", value: s.products_planned },
    { key: "created", value: s.planned_orders_created },
    { key: "updated", value: s.planned_orders_updated },
    { key: "cancelled", value: s.planned_orders_cancelled },
    { key: "messages", value: s.action_messages },
    { key: "optionalSkipped", value: s.optional_lines_skipped },
    { key: "remnantMismatch", value: s.remnant_registry_mismatch },
    { key: "shutdownRequeues", value: s.shutdown_requeues },
  ];
  const shown = cells.filter((c) => c.value !== undefined);
  return (
    <Card title={t("planning.mrpRun.counts")} data-testid="planning-mrp-run-counts">
      {shown.length === 0 ? (
        <p className="planning-note">{t("planning.mrpRun.noCounts")}</p>
      ) : (
        <dl className="planning-facts">
          {shown.map((c) => (
            <Fact key={c.key} label={t(`planning.mrpRun.count.${c.key}`)}>
              <span className="num" data-testid={`planning-mrp-run-count-${c.key}`}>
                {c.value}
              </span>
            </Fact>
          ))}
        </dl>
      )}
      {s.by_kind !== undefined ? (
        <ul className="planning-line-list" data-testid="planning-mrp-run-by-kind">
          {PLANNED_ORDER_KINDS.map((k) => {
            const b = s.by_kind?.[k];
            if (b === undefined) return null;
            return (
              <li key={k} data-kind={k}>
                {t("planning.mrpRun.byKind", {
                  kind: t(`planning.orders.kind.${k}`),
                  planned: b.products_planned,
                  created: b.created,
                  updated: b.updated,
                  cancelled: b.cancelled,
                  messages: b.action_messages,
                })}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Card>
  );
}

function StatLists({ run }: { run: MrpRun }) {
  const { t, i18n } = useTranslation();
  const stats = run.stats;
  const productIds: string[] = [];
  const wcIds = new Set<string>();
  for (const name of RUN_STAT_LISTS) {
    const list = statList(stats, name);
    for (const e of list?.entries ?? []) {
      const entry = e as Record<string, unknown>;
      if (typeof entry["product_id"] === "string") productIds.push(entry["product_id"]);
      if (Array.isArray(entry["product_ids"]))
        for (const p of entry["product_ids"]) if (typeof p === "string") productIds.push(p);
      if (typeof entry["work_center_id"] === "string") wcIds.add(entry["work_center_id"]);
    }
  }
  const products = useProductIndex(productIds);
  const workCenters = useCalendarWorkCenters(wcIds.size > 0 ? run.facility_id : "");
  const { facilities } = usePlanningFacility();
  const sku = (id: string) => products.byId.get(id)?.sku ?? shortId(id);
  const wc = (id: string) => {
    const w = workCenters.data?.find((x) => x.id === id);
    return w === undefined ? shortId(id) : workCenterLabel(w, i18n.language);
  };
  const fac = (id: string) => facilities.find((f) => f.id === id)?.name ?? shortId(id);
  const date = (d: string) => formatDate(d, i18n.language);

  const entryText = (name: RunStatList, raw: unknown): ReactNode => {
    const e = raw as Record<string, unknown>;
    const s = (k: string) => (typeof e[k] === "string" ? e[k] : "");
    switch (name) {
      case "skipped":
        return t("planning.mrpRun.entry.skipped", {
          line: shortId(s("bom_line_id")),
          reason: t(`planning.mrpRun.skipReason.${s("reason")}`),
        });
      case "skipped_work_orders":
        // An open order whose quantity no longer restates, so its component
        // demand is not in this run (spec 03 §3.3e (b), D-407 ruling 3).
        return t("planning.mrpRun.entry.skippedOrder", {
          order: s("wo_no") === "" ? shortId(s("work_order_id")) : s("wo_no"),
          reason: t(`planning.mrpRun.skipReason.${s("reason")}`),
        });
      case "no_bom":
      case "supply_limited":
        return (
          <>
            {sku(s("product_id"))} <ExactQty qty={s("qty")} />{" "}
            {t("planning.mrpRun.entry.from", { date: date(s("first_date")) })}
          </>
        );
      case "defaulted_parameters":
        return t("planning.mrpRun.entry.defaulted", {
          product: sku(s("product_id")),
          kind: t(`planning.orders.kind.${s("order_kind")}`),
          days: typeof e["lead_time_days"] === "number" ? e["lead_time_days"] : 0,
          source: t(`planning.mrpRun.leadSource.${s("lead_time_source")}`),
        });
      case "transfer_source_inactive":
        return t("planning.mrpRun.entry.transferInactive", {
          product: sku(s("product_id")),
          facility: fac(s("source_facility_id")),
        });
      case "depth_exceeded":
      case "bom_cycle":
        return Array.isArray(e["product_ids"])
          ? (e["product_ids"] as unknown[])
              .filter((x): x is string => typeof x === "string")
              .map(sku)
              .join(" → ")
          : "";
      case "byproduct_cycle":
        return sku(s("product_id"));
      case "product_cycles":
        return Array.isArray(raw)
          ? (raw as Record<string, unknown>[])
              .map(
                (p) =>
                  `${fac(typeof p["facility_id"] === "string" ? p["facility_id"] : "")}: ${sku(
                    typeof p["product_id"] === "string" ? p["product_id"] : "",
                  )}`,
              )
              .join(" → ")
          : "";
      case "uncalendared_work_centers":
      case "closed_work_centers":
        return wc(s("work_center_id"));
      case "closed_facility_windows":
        return t("planning.mrpRun.entry.window", {
          facility: fac(s("facility_id")),
          from: date(s("from")),
          to: date(s("to")),
        });
      case "mps_exceeded":
        return (
          <>
            {sku(s("product_id"))}{" "}
            {t("planning.mrpRun.entry.week", { date: date(s("bucket_start")) })}{" "}
            <ExactQty qty={s("excess")} signed />
          </>
        );
      case "mps_unplanned":
        return t("planning.mrpRun.entry.unplanned", {
          product: sku(s("product_id")),
          reason: t(`planning.mrpRun.unplannedReason.${s("reason")}`),
        });
      default:
        return "";
    }
  };

  const present = RUN_STAT_LISTS.flatMap((name) => {
    const list = statList(stats, name);
    return list === undefined ? [] : [{ name, ...list }];
  });

  return (
    <Card title={t("planning.mrpRun.lists")} data-testid="planning-mrp-run-lists">
      {present.length === 0 ? (
        <p className="planning-note">{t("planning.mrpRun.noLists")}</p>
      ) : (
        <div className="planning-run-lists">
          {present.map((list) => (
            <section
              key={list.name}
              className="planning-run-list"
              data-testid="planning-mrp-run-list"
              data-list={list.name}
              data-count={list.count}
            >
              <h3>
                {t(`planning.mrpRun.list.${list.name}`)}{" "}
                <span className="num planning-muted">· {list.count}</span>
              </h3>
              {list.count === 0 ? (
                <p className="planning-muted">{t("planning.mrpRun.none")}</p>
              ) : (
                <ul className="planning-line-list">
                  {list.entries.map((entry, i) => (
                    <li key={String(i)}>{entryText(list.name, entry)}</li>
                  ))}
                </ul>
              )}
              {list.count > list.entries.length ? (
                <p className="planning-muted" data-testid="planning-mrp-run-list-more">
                  {t("planning.mrpRun.more", { count: list.count - list.entries.length })}
                </p>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}
